/**
 * React Query Hooks pour les données principales
 *
 * Hooks avec cache pour ApiService (classes, matières, chapitres, leçons, exercices)
 *
 * CACHE HIÉRARCHIQUE EN CASCADE :
 * - Au démarrage : charger tous les exercices (cache global)
 * - Filtre appliqué : affichage instantané depuis cache parent + requête API en parallèle
 * - Remonter dans la hiérarchie : utiliser cache existant (pas de requête)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminUsersApiService from '../services/adminUsersApi';
import ChapitresLeconsApiService from '../services/chapitresLeconsApi';
import CurriculumApiService from '../services/curriculumApi';
import ExercicesApiService from '../services/exercicesApi';
import { CACHE_TTL, dataKeys } from '../config/queryConfig';
import type { Classe, Matiere, Exercice } from '../types';
import { AppEnvironment } from '../config/environment';

// ============================================================================
// Types pour le cache hiérarchique
// ============================================================================

export type ExerciceApiFilters = {
  classeId?: string;
  matiereId?: string;
  chapitreId?: string;
  leconId?: string;
};

export interface HierarchyMaps {
  chapitresMap: Map<string, { matiereId: string }>;
  matieresMap: Map<string, { classeId: string }>;
}

// ============================================================================
// Fonctions utilitaires pour le cache hiérarchique
// ============================================================================

/**
 * Retourne les filtres du niveau parent pour construire la clé de cache
 * Permet de trouver le cache parent à utiliser comme placeholderData
 */
export function getParentCacheFilters(
  filters: ExerciceApiFilters
): ExerciceApiFilters | null {
  if (filters.leconId) {
    // Parent = sans leconId
    const { leconId: _, ...parent } = filters;
    return parent;
  }
  if (filters.chapitreId) {
    // Parent = sans chapitreId
    const { chapitreId: _, leconId: __, ...parent } = filters;
    return parent;
  }
  if (filters.matiereId) {
    // Parent = sans matiereId
    const { matiereId: _, chapitreId: __, leconId: ___, ...parent } = filters;
    return parent;
  }
  if (filters.classeId) {
    // Parent = cache global (filtres vides)
    return {};
  }

  // Aucun filtre = pas de parent
  return null;
}

/**
 * Filtre les exercices localement selon les filtres spécifiés
 * Utilisé pour générer le placeholderData depuis le cache parent
 */
export function filterExercicesLocally(
  exercices: Exercice[],
  filters: ExerciceApiFilters,
  hierarchyMaps?: HierarchyMaps
): Exercice[] {
  return exercices.filter(ex => {
    // Filtre par leçon (le plus précis)
    if (filters.leconId && ex.leconId !== filters.leconId) return false;

    // Filtre par chapitre
    if (filters.chapitreId && ex.chapitreId !== filters.chapitreId) return false;

    // Filtre par matière (nécessite lookup via chapitre)
    if (filters.matiereId && hierarchyMaps) {
      const chapitre = hierarchyMaps.chapitresMap.get(ex.chapitreId);
      if (!chapitre || chapitre.matiereId !== filters.matiereId) return false;
    }

    // Filtre par classe (nécessite lookup via chapitre -> matière)
    if (filters.classeId && hierarchyMaps) {
      const chapitre = hierarchyMaps.chapitresMap.get(ex.chapitreId);
      if (!chapitre) return false;
      const matiere = hierarchyMaps.matieresMap.get(chapitre.matiereId);
      if (!matiere || matiere.classeId !== filters.classeId) return false;
    }

    return true;
  });
}

// ============================================================================
// Classes
// ============================================================================

export const useClasses = (env: AppEnvironment) => {
  return useQuery({
    queryKey: dataKeys.classes(env),
    queryFn: () => CurriculumApiService.getClasses(env),
    staleTime: CACHE_TTL.STATIC,
  });
};

export const useClasse = (env: AppEnvironment, id: string) => {
  return useQuery({
    queryKey: dataKeys.classe(id, env),
    queryFn: () => CurriculumApiService.getClasseById(env, id),
    staleTime: CACHE_TTL.STATIC,
    enabled: !!id,
  });
};

export const useCreateClasse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, env }: { data: Omit<Classe, 'id'>; env: AppEnvironment }) =>
      CurriculumApiService.createClasse(env, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.classes(variables.env) });
    },
  });
};

export const useUpdateClasse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, env }: { id: string; data: Partial<Classe>; env: AppEnvironment }) =>
      CurriculumApiService.updateClasse(env, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.classes(variables.env) });
      queryClient.invalidateQueries({ queryKey: dataKeys.classe(variables.id, variables.env) });
    },
  });
};

export const useDeleteClasse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, env }: { id: string; env: AppEnvironment }) =>
      CurriculumApiService.deleteClasse(env, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.classes(variables.env) });
    },
  });
};

// ============================================================================
// Matières
// ============================================================================

export const useMatieres = (env: AppEnvironment) => {
  return useQuery({
    queryKey: dataKeys.matieres(env),
    queryFn: () => CurriculumApiService.getMatieres(env),
    staleTime: CACHE_TTL.STATIC,
  });
};

export const useCreateMatiere = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, env }: { data: Omit<Matiere, 'id'>; env: AppEnvironment }) =>
      CurriculumApiService.createMatiere(env, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.matieres(variables.env) });
    },
  });
};

export const useUpdateMatiere = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, env }: { id: string; data: Partial<Matiere>; env: AppEnvironment }) =>
      CurriculumApiService.updateMatiere(env, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.matieres(variables.env) });
      queryClient.invalidateQueries({ queryKey: dataKeys.matiere(variables.id, variables.env) });
    },
  });
};

export const useDeleteMatiere = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, env }: { id: string; env: AppEnvironment }) =>
      CurriculumApiService.deleteMatiere(env, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.matieres(variables.env) });
    },
  });
};

// ============================================================================
// Chapitres
// ============================================================================

export const useChapitres = (env: AppEnvironment, options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: dataKeys.chapitres(env),
    queryFn: () => ChapitresLeconsApiService.getChapitres(env),
    staleTime: CACHE_TTL.SEMI_STATIC,
    enabled: options.enabled ?? true,
  });
};

// ============================================================================
// Leçons
// ============================================================================

export const useLecons = (env: AppEnvironment, options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: dataKeys.lecons(env),
    queryFn: () => ChapitresLeconsApiService.getLecons(env),
    staleTime: CACHE_TTL.SEMI_STATIC,
    enabled: options.enabled ?? true,
  });
};

export const useDeleteLecon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, env }: { id: string; env: AppEnvironment }) =>
      ChapitresLeconsApiService.deleteLecon(env, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.lecons(variables.env) });
    },
  });
};

// ============================================================================
// Exercices
// ============================================================================

export const useExercices = (env: AppEnvironment) => {
  return useQuery({
    queryKey: dataKeys.exercices(env),
    queryFn: () => ExercicesApiService.getExercices(env),
    staleTime: CACHE_TTL.DYNAMIC,
  });
};

export const useDeleteExercice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, env }: { id: string; env: AppEnvironment }) =>
      ExercicesApiService.deleteExercice(env, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataKeys.exercices(variables.env) });
    },
  });
};

/**
 * Hook pour charger les exercices avec filtres et CACHE HIÉRARCHIQUE
 *
 * Comportement :
 * - Si filtre appliqué : affiche immédiatement les données du cache parent filtrées localement
 * - En parallèle : requête API pour confirmer/mettre à jour
 * - Loader uniquement si aucun cache disponible
 */
export const useExercicesByFilters = (
  env: AppEnvironment,
  filters: ExerciceApiFilters = {},
  options: {
    enabled?: boolean;
    hierarchyMaps?: HierarchyMaps;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: dataKeys.exercicesByFilters(env, filters),
    queryFn: () => ExercicesApiService.getExercicesByFilters(env, filters),
    staleTime: CACHE_TTL.DYNAMIC,
    enabled: options.enabled !== false,

    // CACHE HIÉRARCHIQUE : utiliser le cache parent comme placeholder
    placeholderData: () => {
      // Trouver les filtres du niveau parent
      const parentFilters = getParentCacheFilters(filters);
      if (parentFilters === null) return undefined;

      // Déterminer la clé du cache parent
      const isGlobalCache = Object.keys(parentFilters).length === 0;
      const parentKey = isGlobalCache
        ? dataKeys.exercices(env)
        : dataKeys.exercicesByFilters(env, parentFilters);

      // Récupérer les données du cache parent
      const parentData = queryClient.getQueryData<Exercice[]>(parentKey);
      if (!parentData || parentData.length === 0) return undefined;

      // Filtrer localement les données du cache parent
      return filterExercicesLocally(parentData, filters, options.hierarchyMaps);
    },
  });
};

// ============================================================================
// Admin Users
// ============================================================================

export const useAdminUsers = (params?: { role?: string; status?: string; limit?: number; search?: string }) => {
  return useQuery({
    queryKey: dataKeys.adminUsers(params),
    queryFn: () => AdminUsersApiService.getAdminUsers(params),
    staleTime: CACHE_TTL.DYNAMIC,
  });
};

